import { Segmented } from 'antd';
import dayjs from 'dayjs';
import { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { formatDate } from 'src/utils/date-util';

import useProductSupplierInventoryReceipts from 'src/hooks/subscription/useProductSupplierInventoryReceipts';
import useSWRProductSupplierInventoryReceipt from 'src/hooks/swr/useSWRProductSupplierInventoryReceipt';
import useSWRProductSupplierReceiptDates from 'src/hooks/swr/useSWRProductSupplierReceiptDates';
import { useTitle } from 'src/hooks/useTitle';

import ProductSupplierSelector from 'src/atomic-components/atoms/Seletor/ProductSupplierSelector';
import ProductSupplierInventoryReceiptTable from 'src/atomic-components/organisms/Tables/ProductSupplierInventoryReceiptTable';
import ProductSupplierReceiptDateTable from 'src/atomic-components/organisms/Tables/ProductSupplierReceiptDateTable';
import ProductSupplierReceiptTransactionTable from 'src/atomic-components/organisms/Tables/ProductSupplierReceiptTransactionTable';
import DatePickerWithArrows from 'src/components/Common/DatePickerWithArrows/DatePickerWithArrows';
import Loading from 'src/components/Loading/Loading';

import styles from './InventorySupplierInboundBoard.module.scss';

type SearchMode = 'date' | 'supplier';

const searchModeOptions = [
  { label: '날짜별', value: 'date' },
  { label: '매입처별', value: 'supplier' },
];

const InventorySupplierInboundListBoard: FC = () => {
  useTitle('거래처 입고 조회');
  const navigate = useNavigate();
  const { supplierReceiptId } = useParams();
  const {
    supplierInventoryReceipt,
    isLoading,
    refresh: refreshSupplierInventoryReceipt,
  } = useSWRProductSupplierInventoryReceipt(supplierReceiptId ?? null);
  const [searchMode, setSearchMode] = useState<SearchMode>('date');
  const [date, setDate] = useState<dayjs.Dayjs | null>(dayjs());
  const transactionDate = formatDate(date?.toDate() ?? new Date(), 'yyyy-MM-dd');
  const productSupplierInventoryReceipts = useProductSupplierInventoryReceipts(transactionDate);
  const [supplierId, setSupplierId] = useState<string | undefined>();
  const {
    rows: supplierReceiptDateRows,
    isLoading: isLoadingSupplierReceiptDates,
    refresh: refreshSupplierReceiptDates,
  } = useSWRProductSupplierReceiptDates(searchMode === 'supplier' ? supplierId ?? null : null);

  const handleSupplierReceiptChange = (supplierReceiptId: string) => {
    navigate(`/inventory/supplier-inbound-list/${supplierReceiptId}`);
  };

  return (
    <div className='height100 flexColumn'>
      <div className={styles.header}>
        <div className={styles.headerControls}>
          <Segmented
            options={searchModeOptions}
            value={searchMode}
            onChange={(value) => setSearchMode(value as SearchMode)}
          />
          {searchMode === 'date' ? (
            <DatePickerWithArrows date={date} setDate={setDate} />
          ) : (
            <ProductSupplierSelector productSupplierId={supplierId} onProductSupplierChange={setSupplierId} />
          )}
        </div>
      </div>

      <div className='flexRow flex1'>
        <div className='flex1 height100 flexColumn' style={{ maxWidth: 420 }}>
          {searchMode === 'date' ? (
            <ProductSupplierInventoryReceiptTable
              rowData={productSupplierInventoryReceipts}
              onRowClick={(row) => handleSupplierReceiptChange(row._id)}
            />
          ) : (
            <ProductSupplierReceiptDateTable
              rowData={supplierReceiptDateRows}
              onRowClick={(row) => handleSupplierReceiptChange(row.id)}
            />
          )}
        </div>
        <div className='flex2 height100 flexColumn'>
          {supplierInventoryReceipt && (
            <ProductSupplierReceiptTransactionTable
              receiptId={supplierInventoryReceipt._id}
              transactionIds={supplierInventoryReceipt.inboundTransactionIds}
              checkList={supplierInventoryReceipt.checkList}
              onCheckListUpdated={() => {
                refreshSupplierInventoryReceipt();
                refreshSupplierReceiptDates();
              }}
            />
          )}
        </div>
      </div>

      {(isLoading || isLoadingSupplierReceiptDates) && <Loading title='잠시만 기다려주세요.' />}
    </div>
  );
};

export default InventorySupplierInboundListBoard;
